import { useManager } from 'dreamstate';
import React, { useMemo, useState } from 'react';

import { Flex } from '@vimeo/bokeh';
import { Chats } from '@vimeo/bokeh-icons';
import { IBaseDomElementProps } from '@vimeo/core/types/dom';
import { Id, Optional } from '@vimeo/core/types/generic';
import { useLiveGlobals } from '@vimeo/live-core/components/providers/globals';
import { ESidebarAttach, ResponsiveSidebar } from '@vimeo/live-core/components/sidebar';
import { useIsPortraitScreenOrientation } from '@vimeo/live-core/hooks/dom/useIsPortraitScreenOrientation';
import {
  IInteractionSessionContext,
  InteractionSessionManager,
} from '@vimeo/live-core/store/interaction/session';
import { T_JOIN } from '@vimeo/live-core/translations';
import {
  EInteractionModule,
  IInteractionToolsItemDescriptor,
} from '@vimeo/live-core/types/interaction_tools';
import { IInteractionStoreInitialState } from '@vimeo/live-core/types/store';

import { createInteractionDomName } from '../../../../../libs/shared/utils/dom/identifying';
import { InteractionLoginLayout } from './InteractionLoginLayout';

interface IInteractionLoginLayoutProps extends IBaseDomElementProps {
  interactionSessionContext?: IInteractionSessionContext;
}

/**
 * Layout to perform that could be collapsed login in interaction rooms.
 */
export function InteractionCollapsibleLoginLayout({
  id = createInteractionDomName('widget-collapsible-login'),
  className = createInteractionDomName('widget-collapsible-login'),
  // eslint-disable-next-line react-hooks/rules-of-hooks
  interactionSessionContext: { roomUser: { value: roomUser } } = useManager(
    InteractionSessionManager,
  ),
}: IInteractionLoginLayoutProps) {
  const {
    initialState: { interaction },
  } = useLiveGlobals<IInteractionStoreInitialState>();

  const attach: ESidebarAttach = interaction?.toolbar?.attachPosition || ESidebarAttach.RIGHT;
  const isPortrait: boolean = useIsPortraitScreenOrientation();
  const sidebarState = useState<Optional<Id>>(null);
  const isCollapsibleSignIn = Boolean(!roomUser && interaction?.auth?.isCollapsibleSignIn);
  const isLoginVisible: boolean =
    sidebarState[0] === EInteractionModule.LOGIN || isCollapsibleSignIn;
  const isManagementAccessed: boolean = Boolean(
    roomUser?.isCreator && !interaction?.feature?.isManagementDisabled,
  );

  const items = useMemo(() => {
    const loginItem: IInteractionToolsItemDescriptor = {
      id: EInteractionModule.LOGIN,
      label: T_JOIN,
      icon: <Chats />,
      isClosable: true,
      header: T_JOIN,
      headerBackground: 'surface',
      Content: InteractionLoginLayout,
    };

    return isLoginVisible ? [loginItem] : [];
  }, [isLoginVisible]);

  return (
    <Flex
      id={createInteractionDomName(id, 'main')}
      className={createInteractionDomName(className, 'main')}
      position={'relative'}
      height={'100%'}
      width={'100%'}
      flexGrow={1}
      overflow={'hidden'}
      background={'surface'}
      maxWidth={'100%'}
      flexDirection={
        attach === ESidebarAttach.TOP || attach === ESidebarAttach.BOTTOM || isPortrait
          ? 'column'
          : 'row'
      }
      justifyContent={
        attach === ESidebarAttach.TOP || (attach === ESidebarAttach.LEFT && !isPortrait)
          ? 'flex-start'
          : 'flex-end'
      }
    >
      <ResponsiveSidebar
        id={id}
        attach={attach}
        items={items}
        initialStateValue={EInteractionModule.LOGIN}
        isWithHeader={isManagementAccessed || attach !== ESidebarAttach.TOP}
        isPortrait={isPortrait}
        isInactiveDisabled={interaction?.isStandalone}
        isFullscreen={interaction?.toolbar?.panelIsFluid}
        maxSize={interaction?.toolbar?.panelMaxSize}
        state={sidebarState}
        onOpen={interaction?.toolbar?.panelOnOpen}
        onClose={interaction?.toolbar?.panelOnClose}
        onToggleExtend={interaction?.toolbar?.panelOnToggleExtend}
      />
    </Flex>
  );
}
